import React, { useEffect, useMemo, useState } from 'react'
import {
  TextField,
  Button,
  Box,
  IconButton,
  Card,
  CardHeader,
  CardContent,
  Stack,
  Typography,
  Checkbox,
  Tabs,
  Tab,
} from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import { useMutation, useQuery } from '@apollo/client'
import { useParams } from 'react-router'

import { mutations, queries } from '../../graphql'

const DEFAULT_REASONS = ['General Support']

function DonationReasons() {
  const [tabIndex, setTabIndex] = useState(0)
  const { id: charityId } = useParams()
  const {
    data: { charity } = {},
    charityLoading,
    refetch: refetchCharity,
  } = useQuery(queries.charities.myCharityActivity, {
    variables: { charityId },
    fetchPolicy: 'network-only',
  })
  const [deleteReasonId, setDeleteReasonId] = useState(null)
  const [donationReasons, setDonationReasons] = useState([])
  const maxReasons = 100
  const remainingReasons = charity?.purposeOfGifts ? maxReasons - charity.purposeOfGifts.length : maxReasons

  const isDirty = useMemo(
    () =>
      donationReasons.some((r) => {
        const original = charity?.purposeOfGifts.find((o) => o.id === r.id)
        return original && (r.reason !== original.reason || r.active !== original.active)
      }),
    [donationReasons, JSON.stringify(charity?.purposeOfGifts)]
  )

  const [createPurposeOfGift] = useMutation(mutations.charities.createPurposeOfGift, {
    variables: {
      data: {
        reason: '',
        charityId: charity?.id,
      },
    },
    refetchQueries: [
      {
        query: queries.charities.myCharityActivity,
        variables: { charityId },
      },
    ],
  })
  const [deletePurposeOfGift] = useMutation(mutations.charities.deletePurposeOfGift, {
    variables: {
      id: deleteReasonId,
    },
    refetchQueries: [
      {
        query: queries.charities.myCharityActivity,
        variables: { charityId },
      },
    ],
  })
  const [updatePurposeOfGift] = useMutation(mutations.charities.updatePurposeOfGift, {
    refetchQueries: [
      {
        query: queries.charities.myCharityActivity,
        variables: { charityId },
      },
    ],
  })

  const handleUpdateReason = async () => {
    try {
      const changed = donationReasons.filter((r) => {
        const original = charity?.purposeOfGifts.find((o) => o.id === r.id)
        return original && (r.reason !== original.reason || r.active !== original.active)
      })

      if (changed.length === 0) return

      await updatePurposeOfGift({
        variables: {
          data: changed.map(({ id, reason, active }) => ({ id, reason, active })),
        },
      })
      await refetchCharity()
    } catch (err) {
      console.error('updatePurposeOfGift failed:', err)
    }
  }

  useEffect(() => {
    if (!charity?.purposeOfGifts) return

    const sorted = [...charity.purposeOfGifts].sort((a, b) => {
      const aIndex = DEFAULT_REASONS.indexOf(a.reason)
      const bIndex = DEFAULT_REASONS.indexOf(b.reason)
      if (aIndex === -1 && bIndex === -1) return 0
      if (aIndex === -1) return 1
      if (bIndex === -1) return -1
      return aIndex - bIndex
    })

    setDonationReasons(sorted)
  }, [charity])

  useEffect(() => {
    if (deleteReasonId) {
      deletePurposeOfGift()
    }
  }, [deleteReasonId])

  const handleReasonChange = (id, event, toggle = false) => {
    const reason = donationReasons.find((r) => r.id === id)
    if (!reason || DEFAULT_REASONS.includes(reason.reason)) return

    const updatedReason = {
      ...reason,
      [toggle ? 'active' : 'reason']: toggle ? event.target.checked : event.target.value,
    }

    setDonationReasons((prev) => prev.map((r) => (r.id === id ? updatedReason : r)))
  }

  const handleAddReason = () => {
    if (donationReasons.length < maxReasons) {
      createPurposeOfGift()
    }
  }

  const handleRemoveReason = (id) => {
    const reasonToDelete = donationReasons.find((r) => r.id === id)

    if (DEFAULT_REASONS.includes(reasonToDelete?.reason)) return

    setDonationReasons((prev) => prev.filter((r) => r.id !== id))
    setDeleteReasonId(id)
  }

  const handleTabChange = (event, newIndex) => {
    setTabIndex(newIndex)
  }

  const customTab = () => {
    if (!donationReasons || donationReasons.length === 0) {
      return ''
    }
    return (
      <>
        {donationReasons.map((entry, index) => {
          const isDefault = DEFAULT_REASONS.includes(entry.reason)

          return (
            <Box key={entry.id} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <TextField
                fullWidth
                label={`Designation #${index + 1}`}
                variant="outlined"
                value={entry.reason}
                onChange={(event) => handleReasonChange(entry.id, event)}
                disabled={isDefault}
              />
              <Checkbox
                checked={entry.active}
                disabled={isDefault}
                onChange={(event) => handleReasonChange(entry.id, event, true)}
              />
              {!isDefault && (
                <IconButton onClick={() => handleRemoveReason(entry.id)} color="error" sx={{ ml: 1 }}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          )
        })}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAddReason}
            disabled={donationReasons.length >= maxReasons}
          >
            Add New
          </Button>
          <Typography variant="caption" color="text.secondary">
            You can add <b>{remainingReasons}</b> more Gift Designations.
          </Typography>
          <Button variant="contained" color="primary" onClick={handleUpdateReason} disabled={!isDirty}>
            Save
          </Button>
        </Stack>
      </>
    )
  }

  const defualtTab = () => (
    <>
      <br />
      {donationReasons
        ? donationReasons
            .filter((r) => r.isDefault)
            .map((entry, index) => (
              <Box key={entry.id} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <Checkbox checked={entry.active} onChange={(event) => handleReasonChange(entry.id, event, true)} />
                <TextField
                  fullWidth
                  label={`Designation #${index + 1}`}
                  variant="outlined"
                  value={entry.reason}
                  onChange={(event) => handleReasonChange(entry.id, event)}
                />
                {charity?.purposeOfGifts?.length > 0 && (
                  <IconButton onClick={() => handleRemoveReason(entry.id)} color="error" sx={{ ml: 1 }}>
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))
        : ''}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpdateReason}
          sx={{
            display: isDirty ? 'auto' : 'none',
          }}
        >
          Save
        </Button>
      </Stack>
    </>
  )

  return (
    <Box sx={{ maxWidth: 560, mt: 3 }}>
      <Card>
        <CardHeader
          title="Purpose Of Gift"
          subheader={`Add or update the specific purposes and programs for which your charity accepts donations.
          These designations will restrict the gift to the specified purpose.`}
        />
        <CardContent>{customTab()}</CardContent>
      </Card>
    </Box>
  )
}

export default DonationReasons
